import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Switching who is holding the device.
//
// Built for a phone in a factory: big targets, no typing, one hand. The keypad
// is deliberately not a text input — a numeric field on mobile still brings up a
// keyboard that covers half the screen, and people wearing gloves miss its keys.

const TEAL   = '#14b8a6';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const CARD   = '#1e2329';
const SUNK   = '#171b21';
const RAISE  = '#232a31';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';

const PIN_MIN = 4;
const PIN_MAX = 8;

const LEVEL_LABEL = { director: 'Director', setter: 'Setter', operator: 'Operator' };

export default function SwitchUserModal({ onClose }) {
  const { login, user } = useAuth();

  const [people, setPeople]   = useState(null);   // null = still loading
  const [target, setTarget]   = useState(null);
  const [pin, setPin]         = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [left, setLeft]       = useState(null);   // attempts remaining

  useEffect(() => {
    api.get('/api/v1/auth/switchable')
      .then(r => setPeople(r.data || []))
      .catch(e => { setPeople([]); setErr(e.response?.data?.error || 'Could not load the list'); });
  }, []);

  const submit = useCallback(async (code) => {
    if (!target || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.post('/api/v1/auth/switch', { userId: target.id, pin: code });
      login(r.data.token, r.data.user);
      onClose();
      // Land on a clean state rather than whatever the previous person was doing.
      window.location.href = '/';
    } catch (e) {
      const d = e.response?.data || {};
      setErr(d.error || 'Could not switch');
      setLeft(typeof d.attempts_left === 'number' ? d.attempts_left : null);
      if (d.locked) setTarget(t => (t ? { ...t, locked: true } : t));
      setPin('');
    } finally {
      setBusy(false);
    }
  }, [target, busy, login, onClose]);

  // Submitting on the last digit saves a tap, which matters when this is done
  // twenty times a week with one hand.
  function press(d) {
    if (busy) return;
    setErr(null);
    const next = (pin + d).slice(0, PIN_MAX);
    setPin(next);
    if (next.length === PIN_MIN) submit(next);
  }

  function choose(p) {
    if (p.locked) return;
    setTarget(p); setPin(''); setErr(null); setLeft(null);
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.card} onClick={e => e.stopPropagation()}>

        <div style={st.head}>
          <div>
            <div style={st.title}>{target ? `Switch to ${target.name}` : 'Switch user'}</div>
            <div style={st.sub}>
              {target
                ? 'Enter their PIN'
                : <>Signed in as <b style={{ color: TEXT }}>{user?.name}</b></>}
            </div>
          </div>
          <button aria-label="Close" style={st.x} onClick={onClose}>✕</button>
        </div>

        <div style={st.body}>
          {!target && (
            <>
              {people === null && <div style={st.note}>Loading…</div>}

              {people !== null && people.length === 0 && (
                <div style={st.empty}>
                  <div style={{ color: TEXT, fontWeight: 600, marginBottom: 6 }}>Nobody else has set a PIN</div>
                  <div>Each person sets their own from <b style={{ color: DIM }}>Account</b>. Until they do,
                  they cannot be switched to on this device.</div>
                </div>
              )}

              {(people || []).map(p => (
                <button
                  key={p.id}
                  onClick={() => choose(p)}
                  disabled={p.locked}
                  style={{ ...st.person, ...(p.locked ? st.personLocked : null) }}
                >
                  <span style={st.avatar} aria-hidden="true">{initials(p.name)}</span>
                  <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                    <span style={st.pname}>{p.name}</span>
                    <span style={st.prole}>{LEVEL_LABEL[p.permissionLevel] || p.role}</span>
                  </span>
                  {p.locked
                    ? <span style={st.lockTag}>PIN locked</span>
                    : <span style={{ color: MUTED, fontSize: 15 }}>›</span>}
                </button>
              ))}
            </>
          )}

          {target && (
            <>
              <div style={st.dots}>
                {Array.from({ length: Math.max(PIN_MIN, pin.length) }).map((_, i) => (
                  <span key={i} style={{ ...st.dot, ...(i < pin.length ? st.dotOn : null) }} />
                ))}
              </div>

              {err && (
                <div style={st.err}>
                  {err}
                  {left != null && left > 0 && (
                    <div style={{ color: MUTED, fontSize: 12, marginTop: 3 }}>
                      {left} {left === 1 ? 'try' : 'tries'} left before it locks
                    </div>
                  )}
                </div>
              )}

              <div style={st.pad}>
                {[1,2,3,4,5,6,7,8,9].map(n => (
                  <button key={n} style={st.key} onClick={() => press(String(n))} disabled={busy}>{n}</button>
                ))}
                <button style={{ ...st.key, ...st.keyQuiet }} onClick={() => { setPin(''); setErr(null); }} disabled={busy}>
                  Clear
                </button>
                <button style={st.key} onClick={() => press('0')} disabled={busy}>0</button>
                <button
                  style={{ ...st.key, ...st.keyGo, ...(pin.length < PIN_MIN ? st.keyOff : null) }}
                  onClick={() => submit(pin)}
                  disabled={busy || pin.length < PIN_MIN}
                >
                  {busy ? '…' : 'Go'}
                </button>
              </div>

              <button style={st.back} onClick={() => { setTarget(null); setPin(''); setErr(null); setLeft(null); }}>
                ‹ Someone else
              </button>
            </>
          )}
        </div>

        <div style={st.foot}>
          A PIN only changes who is using this device. It can never sign in on a new one.
        </div>
      </div>
    </div>
  );
}

function initials(name) {
  return String(name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0].toUpperCase()).join('');
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex',
             alignItems: 'center', justifyContent: 'center', zIndex: 1400, fontFamily: 'system-ui', padding: 16 },
  card:    { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%',
             maxWidth: 380, maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
             gap: 12, padding: '18px 20px 14px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 },
  title:   { fontSize: 16, fontWeight: 700, color: TEXT },
  sub:     { fontSize: 12.5, color: MUTED, marginTop: 2 },
  x:       { background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer', padding: 2, lineHeight: 1 },
  body:    { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' },

  note:    { color: MUTED, fontSize: 13, textAlign: 'center', padding: '20px 0' },
  empty:   { color: MUTED, fontSize: 13, lineHeight: 1.5, background: SUNK,
             border: `1px solid ${BORDER}`, borderRadius: 10, padding: '16px 18px' },

  person:  { display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 58,
             background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 10,
             padding: '10px 14px', cursor: 'pointer', font: 'inherit', color: TEXT },
  personLocked: { opacity: 0.5, cursor: 'not-allowed' },
  avatar:  { width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: RAISE,
             color: TEAL, fontSize: 13, fontWeight: 700, display: 'flex',
             alignItems: 'center', justifyContent: 'center' },
  pname:   { display: 'block', fontSize: 15, fontWeight: 600, color: TEXT,
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  prole:   { display: 'block', fontSize: 11.5, color: MUTED, marginTop: 1 },
  lockTag: { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
             color: AMBER, background: '#3a2c0d', border: '1px solid #6b4a10',
             borderRadius: 5, padding: '3px 7px', whiteSpace: 'nowrap' },

  dots:    { display: 'flex', gap: 10, justifyContent: 'center', padding: '10px 0 4px' },
  dot:     { width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${BORDER}`, background: 'transparent' },
  dotOn:   { background: TEAL, borderColor: TEAL },

  err:     { color: RED, fontSize: 13, textAlign: 'center', lineHeight: 1.4, padding: '2px 4px' },

  pad:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 4 },
  key:     { height: 56, background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 10,
             color: TEXT, fontSize: 21, fontWeight: 600, cursor: 'pointer',
             fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit' },
  keyQuiet:{ fontSize: 13, color: MUTED, fontWeight: 600 },
  keyGo:   { fontSize: 15, background: TEAL, borderColor: TEAL, color: '#04211e', fontWeight: 700 },
  keyOff:  { opacity: 0.35, cursor: 'not-allowed' },

  back:    { marginTop: 10, background: 'none', border: 'none', color: MUTED,
             fontSize: 13, cursor: 'pointer', alignSelf: 'center', font: 'inherit', padding: 6 },

  foot:    { padding: '12px 20px 16px', borderTop: `1px solid ${BORDER}`, color: MUTED,
             fontSize: 11.5, lineHeight: 1.45, textAlign: 'center', flexShrink: 0 },
};
